import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { Step } from 'shared/InstallationDialog';
import { useMultiStepDialog } from 'shared/MultiStepDialog';
import { Checkbox } from 'ui/checkbox';
import { cn } from 'utils/className';
import { spawn } from 'utils/translations';
import { SELECTION_ACTIONS } from '../consts';
import { useGitlabInstallationV2 } from '../gitlabInstallationV2Context';
import {
  ScopeBrowserView,
  useScopeBrowser,
  type ScopeRowState,
} from '../ScopeBrowser';
import { initialSelectionState, selectionReducer } from '../selectionReducer';
import {
  buildInstallSelection,
  isCoveredBySelectedAncestorGroup,
  isCoveredBySelectedGroup,
  isScopeCovered,
  isSelectionEmpty,
} from '../selectionUtils';
import { getSelectErrorCode } from '../utils';
import type {
  GitlabProvisioningSelectGroupItem,
  GitlabProvisioningSelectProjectItem,
} from 'network/httpClients/GitlabClient';

/**
 * Select step — one server-merged paginated list (groups then repos) with
 * drill-down navigation, via the shared `ScopeBrowser`. A group that supports group
 * webhooks is whole-group selectable (its subtree renders read-only "Included via
 * group" once picked); a group that doesn't is drill-only, exposing individual repos
 * (project hooks). Tier is one page-level capability for single-group cloud and
 * self-managed, but resolved per top-level group on gitlab.com multi-group (a Premium
 * group is selectable while a Free one alongside it is drill-only). Already-covered
 * scopes (`coverageState`) are pre-ticked and locked (adopt-into-v2). Search returns
 * scope-wide flat matches across both types. The computed selection is synced to
 * context; Next is gated on a non-empty selection.
 */
export const SelectScope = () => {
  const { setIsNextEnabled } = useMultiStepDialog();
  const {
    accessToken,
    gitlabUrl,
    isMultiGroupCloud,
    setInstallSelection,
    setScopePaths,
  } = useGitlabInstallationV2();

  // PAT + pasted URL for the stateless Select call (server re-derives scope);
  // undefined gates the query.
  const selectContext =
    accessToken !== undefined && gitlabUrl !== ''
      ? ({ kind: 'url', accessToken, gitlabUrl } as const)
      : undefined;

  const spawnSelect = spawn('dialogs.gitlab-installation-v2.steps.select');
  const spawnSelectErrors = spawn(
    'dialogs.gitlab-installation-v2.steps.select.errors',
  );

  const [selection, dispatch] = useReducer(
    selectionReducer,
    initialSelectionState,
  );

  const browser = useScopeBrowser(selectContext);
  const {
    isSearching,
    isDrilledIn,
    currentPage,
    totalPages,
    groupItems,
    projectItems,
    groupWebhooksSupported,
    isLoading,
    isError,
    error,
    groupPathsById,
    projectPathsById,
    topLevelGroupIdByGroupId,
    topLevelGroupIdByProjectId,
    groupWebhooksSupportedByTopLevelGroupId,
  } = browser;

  // Whether a group supports a whole-group webhook (group-hook mode). On gitlab.com
  // multi-group, tier is per top-level group, so it's read from the per-item flag;
  // single-group cloud and self-managed use the one page-level capability.
  const groupSupportsGroupHooks = useCallback(
    (group: GitlabProvisioningSelectGroupItem): boolean =>
      isMultiGroupCloud
        ? group.groupWebhooksSupported ?? false
        : groupWebhooksSupported,
    [isMultiGroupCloud, groupWebhooksSupported],
  );

  // Race-free tier lookups: the persisted (cross-page) maps merged with THIS render's
  // group items, so a just-loaded level resolves synchronously instead of lagging a
  // render behind the effect that persists them.
  const groupWebhooksByTopLevelGroupId = useMemo(() => {
    const merged = new Map(groupWebhooksSupportedByTopLevelGroupId);

    groupItems.forEach((group) => {
      if (
        group.topLevelGroupId !== undefined &&
        group.groupWebhooksSupported !== undefined
      ) {
        merged.set(group.topLevelGroupId, group.groupWebhooksSupported);
      }
    });

    return merged;
  }, [groupWebhooksSupportedByTopLevelGroupId, groupItems]);

  const topLevelGroupIdByGroupIdSync = useMemo(() => {
    const merged = new Map(topLevelGroupIdByGroupId);

    groupItems.forEach((group) => {
      if (group.topLevelGroupId !== undefined) {
        merged.set(group.id, group.topLevelGroupId);
      }
    });

    return merged;
  }, [topLevelGroupIdByGroupId, groupItems]);

  // Whether a selected group id's tier permits a group webhook — the fail-safe gate for
  // the install payload, resolved by id via the race-free merged maps.
  const canGroupHook = useCallback(
    (groupId: number): boolean => {
      if (!isMultiGroupCloud) return groupWebhooksSupported;

      const topLevelGroupId = topLevelGroupIdByGroupIdSync.get(groupId);

      return topLevelGroupId !== undefined
        ? groupWebhooksByTopLevelGroupId.get(topLevelGroupId) ?? false
        : false;
    },
    [
      isMultiGroupCloud,
      groupWebhooksSupported,
      topLevelGroupIdByGroupIdSync,
      groupWebhooksByTopLevelGroupId,
    ],
  );

  // Show group checkboxes at this level only when at least one group here can host a
  // group webhook. An entirely-Free level (e.g. all-Free multi-group) shows none —
  // those groups are drill-only (pick individual repos). A mixed level shows every
  // group's checkbox, disabling the Free ones (see getGroupRow), so the list reads
  // consistently instead of some rows having a checkbox and others not.
  const showGroupCheckboxes = groupItems.some(groupSupportsGroupHooks);

  // The description/title copy is latched on the first resolved level so it can't
  // flicker mid-load (the query keeps no placeholder data, so capability momentarily
  // resets between fetches) and stays stable while drilling. 'group' once any group is
  // selectable at the root, else 'repos'.
  const [descriptionMode, setDescriptionMode] = useState<
    'group' | 'repos' | undefined
  >(undefined);

  useEffect(() => {
    if (isLoading || descriptionMode !== undefined) return;

    setDescriptionMode(showGroupCheckboxes ? 'group' : 'repos');
  }, [isLoading, showGroupCheckboxes, descriptionMode]);

  // Scopes auto-selected via adoption (coverageState), tracked so we adopt once.
  // Keyed by kind ("group-<id>" / "project-<id>") so a group and a project with
  // the same numeric id can't collide in this dedupe set.
  const adoptedRef = useRef<Set<string>>(new Set());

  const selectionCounts = useMemo(
    () => ({
      groups: selection.selectedGroupIds.size,
      repos: selection.selectedProjectIds.size,
    }),
    [selection.selectedGroupIds, selection.selectedProjectIds],
  );

  // Adopt (pre-tick) already-connected scopes as they load. Group mode adopts covered
  // groups; a repo is adopted only for its OWN hook. A `covered_by_group` repo is
  // already covered by an existing group hook, so it renders read-only but must never
  // enter the selection (adopting it would install a redundant project hook) — this is
  // independent of tier, so it can't be tripped by a not-yet-populated capability map.
  useEffect(() => {
    const adoptableGroups = groupItems
      .filter(
        (group) =>
          groupSupportsGroupHooks(group) &&
          isScopeCovered(group.coverageState) &&
          !adoptedRef.current.has(`group-${group.id}`),
      )
      .map((group) => group.id);

    const adoptableRepos = projectItems
      .filter(
        (project) =>
          project.coverageState === 'own_hook' &&
          !adoptedRef.current.has(`project-${project.id}`),
      )
      .map((project) => project.id);

    if (adoptableGroups.length === 0 && adoptableRepos.length === 0) return;

    adoptableGroups.forEach((id) => adoptedRef.current.add(`group-${id}`));
    adoptableRepos.forEach((id) => adoptedRef.current.add(`project-${id}`));
    dispatch({
      type: SELECTION_ACTIONS.PRESELECT,
      groupIds: adoptableGroups,
      projectIds: adoptableRepos,
    });
  }, [groupItems, projectItems, groupSupportsGroupHooks]);

  const installSelection = useMemo(
    () =>
      buildInstallSelection(
        selection,
        groupPathsById,
        canGroupHook,
        projectPathsById,
      ),
    [selection, groupPathsById, canGroupHook, projectPathsById],
  );

  // fullPaths of the currently selected groups — a repo under one of these is
  // included via that group's webhook, so it renders read-only.
  const selectedGroupPaths = useMemo(
    () =>
      Array.from(selection.selectedGroupIds)
        // Only group-hook-capable selections cover their subtree — mirror the payload's
        // canGroupHook filter so UI coverage/locking can never derive from a group that
        // won't actually be installed as a group hook.
        .filter(canGroupHook)
        .map((id) => groupPathsById.get(id))
        .filter((path): path is string => path !== undefined),
    [selection.selectedGroupIds, canGroupHook, groupPathsById],
  );

  useEffect(() => {
    setInstallSelection(installSelection);
    setIsNextEnabled(!isSelectionEmpty(installSelection));
  }, [installSelection, setInstallSelection, setIsNextEnabled]);

  useEffect(() => {
    setScopePaths({
      groupPathsById,
      projectPathsById,
      topLevelGroupIdByGroupId,
      topLevelGroupIdByProjectId,
    });
  }, [
    groupPathsById,
    projectPathsById,
    topLevelGroupIdByGroupId,
    topLevelGroupIdByProjectId,
    setScopePaths,
  ]);

  const emptyMessage = isSearching
    ? spawnSelect('no-results')
    : isDrilledIn
      ? spawnSelect('empty-group')
      : spawnSelect('no-groups');

  const pageLabel =
    totalPages !== undefined
      ? spawnSelect('page-of', { page: currentPage, total: totalPages })
      : spawnSelect('page', { page: currentPage });

  // A typed listing failure (dead/under-scoped token, or a browsed scope gone while
  // paging) gets reason-specific copy and no Retry, since retry can't fix it. Any
  // untyped / transient failure keeps the generic message + Retry.
  const selectErrorCode = getSelectErrorCode(error);
  const typedErrorMessage =
    isError && selectErrorCode !== undefined
      ? spawnSelectErrors(selectErrorCode)
      : undefined;
  const errorMessage = typedErrorMessage ?? spawnSelect('error');
  const retryLabel = typedErrorMessage ? undefined : spawnSelect('retry');

  // Install policy: a scope is locked only when it is actually covered — its own
  // hook (already connected) or an ancestor group hook (existing, or a group
  // selected this session). own_hook and covered_by_group get distinct tooltips;
  // everything else is freely selectable, groups or repos alike.
  const getGroupRow = (
    group: GitlabProvisioningSelectGroupItem,
  ): ScopeRowState => {
    const isOwnHook = group.coverageState === 'own_hook';
    // Covered by an ancestor group hook: a real existing one (BE `covered_by_group`)
    // or a strict-ancestor group selected this session. Its subtree webhook includes
    // this subgroup, so it renders read-only just like a repo below a selected group.
    const isCoveredByGroup =
      group.coverageState === 'covered_by_group' ||
      isCoveredBySelectedAncestorGroup(group.fullPath, selectedGroupPaths);
    // Cloud multi-group: a top-level group the caller doesn't own can't host a service
    // account, so it's fully non-interactive (no select, no drill) with an Owner note.
    const isOwnerGated =
      isMultiGroupCloud && group.canInstallGroupLevel === false;
    // Tier-gated: checkboxes are shown at this level, but this group's plan can't host a
    // group webhook. Its checkbox is disabled with an explanatory tooltip while the row
    // stays drillable (pick individual repos inside). Only when not otherwise locked.
    const isTierGated =
      showGroupCheckboxes &&
      !groupSupportsGroupHooks(group) &&
      !isOwnHook &&
      !isCoveredByGroup &&
      !isOwnerGated;

    return {
      showCheckbox: showGroupCheckboxes,
      checked: isCoveredByGroup || selection.selectedGroupIds.has(group.id),
      disabled: isOwnHook || isCoveredByGroup || isOwnerGated || isTierGated,
      disableDrillIn: isOwnerGated,
      // A tier-gated group grays only its checkbox — the label/icon and drill-in stay
      // actionable. Other disabled states gray the whole locked row (the default).
      dimmed: isTierGated ? false : undefined,
      tooltip: isOwnHook
        ? spawnSelect('already-connected-tooltip')
        : isCoveredByGroup
          ? spawnSelect('covered-by-group-tooltip')
          : isOwnerGated
            ? spawnSelect('requires-owner')
            : isTierGated
              ? spawnSelect('group-webhooks-unsupported-tooltip')
              : undefined,
    };
  };

  const getProjectRow = (
    project: GitlabProvisioningSelectProjectItem,
  ): ScopeRowState => {
    const isOwnHook = project.coverageState === 'own_hook';
    // Covered by an ancestor group hook: a real existing one (BE `covered_by_group`)
    // or a group selected this session. Either way the group's webhook includes it,
    // so it's read-only rather than individually selectable.
    const isGroupCovered =
      project.coverageState === 'covered_by_group' ||
      isCoveredBySelectedGroup(project.pathWithNamespace, selectedGroupPaths);

    return {
      showCheckbox: true,
      checked:
        isOwnHook ||
        isGroupCovered ||
        selection.selectedProjectIds.has(project.id),
      disabled: isOwnHook || isGroupCovered,
      tooltip: isOwnHook
        ? spawnSelect('already-connected-tooltip')
        : isGroupCovered
          ? spawnSelect('covered-by-group-tooltip')
          : undefined,
    };
  };

  // A single always-visible "Select all / Deselect all" control acts on the
  // currently-visible, selectable rows at this level only (not descendants, not
  // other pages): it selects them, or clears them once they are all selected. A row
  // counts as selectable when its computed state shows an enabled checkbox, so
  // locked rows (already connected, included via group, owner-gated) and drill-only
  // groups in repo mode are never touched.
  const pageSelectableGroupIds = groupItems
    .filter((group) => {
      const row = getGroupRow(group);

      return row.showCheckbox && !row.disabled;
    })
    .map((group) => group.id);
  const pageSelectableProjectIds = projectItems
    .filter((project) => {
      const row = getProjectRow(project);

      return row.showCheckbox && !row.disabled;
    })
    .map((project) => project.id);
  const hasSelectableRows =
    pageSelectableGroupIds.length + pageSelectableProjectIds.length > 0;
  const allOnPageSelected =
    hasSelectableRows &&
    pageSelectableGroupIds.every((id) => selection.selectedGroupIds.has(id)) &&
    pageSelectableProjectIds.every((id) =>
      selection.selectedProjectIds.has(id),
    );
  const someOnPageSelected =
    pageSelectableGroupIds.some((id) => selection.selectedGroupIds.has(id)) ||
    pageSelectableProjectIds.some((id) => selection.selectedProjectIds.has(id));
  // Checked when every selectable row on this page is picked, indeterminate on a
  // partial pick — matching the Rules select-all checkbox.
  const selectAllChecked = allOnPageSelected
    ? true
    : someOnPageSelected
      ? 'indeterminate'
      : false;

  const onToggleSelectAllPage = () =>
    dispatch({
      type: allOnPageSelected
        ? SELECTION_ACTIONS.DESELECT
        : SELECTION_ACTIONS.PRESELECT,
      groupIds: pageSelectableGroupIds,
      projectIds: pageSelectableProjectIds,
    });

  const showRepoCount =
    descriptionMode === 'repos' || selectionCounts.repos > 0;

  return (
    <Step
      title={spawnSelect(descriptionMode === 'repos' ? 'title-repos' : 'title')}
      description={
        descriptionMode === undefined
          ? undefined
          : spawnSelect(
              descriptionMode === 'group' ? 'description' : 'description-repos',
            )
      }
    >
      <ScopeBrowserView
        browser={browser}
        labels={{
          searchPlaceholder: spawnSelect('search-placeholder'),
          breadcrumbRoot: spawnSelect('breadcrumb-root'),
          emptyMessage,
          errorMessage,
          retryLabel,
          pageLabel,
          getDrillInLabel: (group) => spawnSelect('drill-in-label', { group }),
        }}
        getGroupRow={getGroupRow}
        getProjectRow={getProjectRow}
        onToggleGroup={(group) =>
          dispatch({ type: SELECTION_ACTIONS.TOGGLE_GROUP, id: group.id })
        }
        onToggleProject={(project) =>
          dispatch({ type: SELECTION_ACTIONS.TOGGLE_PROJECT, id: project.id })
        }
      >
        {/* Selection count — group mode can select whole groups and individual
            repos, so show the group count plus the repo count when any repos are
            picked; repo mode selects individual repos only. The "select all"
            toggle affects only the selectable rows on the current page. */}
        <div className="flex items-center justify-between gap-4 text-sm">
          {/* Gate on the latched descriptionMode, not the raw capability:
              groupWebhooksSupported resets to false on every page/drill fetch
              (no placeholder data), so gating directly would flicker the group
              count away mid-load — the selection itself is unchanged. */}
          <div className="flex gap-4 text-text-general-neutral-subtle">
            {descriptionMode === 'group' && (
              <span>
                {spawnSelect('groups-selected', {
                  count: selectionCounts.groups,
                })}
              </span>
            )}
            {showRepoCount && (
              <span>
                {spawnSelect('total-repos', { count: selectionCounts.repos })}
              </span>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Checkbox
              checked={selectAllChecked}
              disabled={!hasSelectableRows}
              onCheckedChange={onToggleSelectAllPage}
              aria-label={spawnSelect('select-all')}
            />
            <span
              className={cn(
                'text-text-general-neutral-subtle',
                !hasSelectableRows && 'opacity-50',
              )}
            >
              {spawnSelect('select-all')}
            </span>
          </div>
        </div>
      </ScopeBrowserView>
    </Step>
  );
};
